import { forwardRef, useImperativeHandle } from 'react';
import Joyride, { CallBackProps, Step } from 'react-joyride';
import { useNavigate } from 'react-router-dom';
import { routePath } from 'config/routeMgt/routePath';
import './style.css';
import { useTour } from './TourGuideContext';

const FULL_TOUR_STEPS: Step[] = [
    // Dashboard/General Steps
    {
        target: '[data-tour="talent-dashboard"]',
        content: 'Welcome to your talent dashboard! This is your central hub for discovering job opportunities, tracking applications and connecting with top companies.',
        placement: 'right',
        disableBeacon: true,
        styles: {
            options: {
                backgroundColor: '#f4f4f9',
                arrowColor: '#f4f4f9',
                zIndex: 1000
            }
        }
    },
    {
        target: '[data-tour="talent-jobs"]',
        content: 'The "Jobs" section allows you to browse available job opportunities. Let\'s explore it now!',
        placement: 'right',
        disableBeacon: true,
    },

    // Jobs Page Steps
    {
        target: "[data-tour='job-board-title']",
        content: "Welcome to the Job Board! Here you can find and apply to jobs.",
        placement: "center",
        disableBeacon: true,
    },
    {
        target: "[data-tour='job-tabs']",
        content: "Switch between different job views: All Jobs, Liked Jobs (applied), Bookmarked Jobs, and Suggested Jobs.",
        disableBeacon: true,
    },
    // {
    //     target: ".demo-job-row",
    //     content: "This is a job listing. Click on the job title to view details.",
    //     disableBeacon: true,
    // },
    // {
    //     target: "[data-joyride='apply-job']",
    //     content: "Click the heart icon to like (apply for) a job. Note: You need at least 75% profile completion to apply.",
    //     disableBeacon: true,
    // },
    // {
    //     target: "[data-joyride='bookmark-job']",
    //     content: "Click the bookmark icon to save a job for later.",
    //     disableBeacon: true,
    // },

    // Invites Page Steps
    {
        target: '[data-tour="talent-invites"]',
        content: 'This is where you can manage your job invitations. Let\'s check it out!',
        placement: 'right',
        disableBeacon: true,
    },
    {
        target: '[data-tour="talent-invites"]',
        content: 'This is where you can manage your job invitations. View details of the job offers you\'ve received and decide whether to accept or decline them.',
        placement: 'right',
        disableBeacon: true,
    }

];



export const TalentTourGuide = forwardRef((_, ref) => {
    const { running, activeTour, stepIndex, start, finish, setStepIndex } = useTour();
    const navigate = useNavigate();

    useImperativeHandle(ref, () => ({
        start: () => start('talent')
    }));

    const onCallback = (data: CallBackProps) => {
        const { index, action, status, type } = data;

        if (action === 'close') {
            finish();
            return;
        }

        if (type === 'step:after') {
            const nextIndex = action === 'prev' ? index - 1 : index + 1;
            const nextStep = FULL_TOUR_STEPS[nextIndex];

            switch (nextIndex) {
                case 0:
                case 1:
                    navigate(routePath.home);
                    break;
                case 2:
                case 3:
                    navigate(routePath.jobs);
                    break;
                case 4:
                case 5:
                    navigate(routePath.invites);
                    break;
            }

            const target =
                typeof nextStep?.target === 'string' ? nextStep.target : '';
            let attempts = 0;
            const moveToStep = () => {
                if (!target || document.querySelector(target) || attempts > 20) {
                    setStepIndex(nextIndex);
                    return;
                }
                attempts += 1;
                window.setTimeout(moveToStep, 50);
            };
            moveToStep();
        }

        if (status === 'finished' || status === 'skipped' || status === 'error') {
            finish();
        }
    };

    return (
        <Joyride
            steps={FULL_TOUR_STEPS}
            run={running && activeTour === 'talent'}
            continuous
            stepIndex={stepIndex}
            showSkipButton
            showProgress={false}
            callback={onCallback}
            disableScrolling
            disableOverlayClose
            // debug={true}
            styles={{
                options: {
                    primaryColor: '#007bff',
                    overlayColor: 'rgba(0, 0, 0, 0.5)',
                    textColor: '#333',
                    zIndex: 10000,
                    arrowColor: '#007bff',
                    beaconSize: 40,
                },
                tooltip: {
                    padding: '16px 20px',
                    fontSize: '15px',
                    maxWidth: '350px',
                    backgroundColor: '#ffffff',
                    color: '#333',
                    boxShadow: '0px 6px 15px rgba(0, 0, 0, 0.2)',
                    borderRadius: '8px',
                },
                tooltipContent: {
                    padding: '10px 0',
                },
                buttonNext: {
                    backgroundColor: '#007bff',
                    color: '#fff',
                    borderRadius: '4px',
                    padding: '8px 16px',
                },
                buttonBack: {
                    color: '#007bff',
                }
            }}
        />
    );
});